import { ButtonSecondary } from "components/Atoms/Button";
import { SearchInput } from "components/Atoms/SearchInput";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import React from "react";
import { useDebounce } from "utilities/usehooks";
import Panel from "../../../components/panels/panel";
import { useCustomQuery, useManualPagination } from "../../../utilities/hookutils";
import AddNewModal from "./AddNewModal";
import ContactWithWorkRelationRowCard from "./ContactRowCard";

interface Props {
  company: server.dto.Company
  panelConfig: server.dto.LinkedContactPanelConfig
}

const LinkedContacts = (props: Props) => {
  const perPage = 10;
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const [hasMoreThan10Contacts, setHasMoreThan10Contacts] = React.useState<boolean>(false);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [displayNewContactModal, setDisplayNewContactModal] = React.useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery);
  const [instruction, setInstruction] = React.useState<server.dto.ListContactByCompanyInstruction>({
    companyId: props.company.companyId,
    page: 1,
    perPage,
    keyword: debouncedSearchQuery
  });

  const api = useCustomQuery({
    queryKey: ["linked-contacts-to-company", instruction],
    queryFn: () => window.CoreApi.CompanyToContact.ListContactByCompany(instruction),
    onSuccess: (res) => {
      pagination.setPagination(res.data.pagination);
      if (isInitialLoading) {
        if (res.data.pagination.total > 10) {
          setHasMoreThan10Contacts(true);
        }
      }

      setIsInitialLoading(false);
    }
  });

  const paginate = async (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({ ...instruction, page: paginationData.page, perPage: paginationData.perPage });
  };

  const pagination = useManualPagination(1, perPage, paginate);
  const items = api.data?.data?.data || [];

  React.useEffect(() => {
    setInstruction({
      ...instruction,
      keyword: debouncedSearchQuery,
      page: 1
    });
  }, [debouncedSearchQuery]);

  const onAddNewContactClosed = (anythingChanged: boolean) => {
    setDisplayNewContactModal(false);
    if (anythingChanged) {
      api.refetch();
    }
  };

  const { refToScrollTo } = useShortcutPanel({
    type: "contact",
    label: props.panelConfig.headerLabel,
    totalItems: pagination.total,
    onAddClicked: props.panelConfig.canAdd ? () => { setDisplayNewContactModal(true); } : null
  });

  return <>
    {displayNewContactModal && <AddNewModal
      company={props.company}
      onClose={onAddNewContactClosed}
      excludeKeys={items.map(c => c.contact.contactId)}
    />}

    <Panel
      panelClassName="s1_related-feature-panel"
      panelRef={refToScrollTo}
      title={props.panelConfig.headerLabel}
      isLoading={isInitialLoading || (api.isFetching && items.length === 0)}
      pagination={pagination.Component}
      button={props.panelConfig.canAdd
        ? <ButtonSecondary
          onClick={() => { setDisplayNewContactModal(true); }}
          label={RESX.GeneralLabels.resxNew} />
        : null
      }
    >
      {hasMoreThan10Contacts && <SearchInput
        onChange={value => { setSearchQuery(value); }}
        value={searchQuery}
        placeholder={RESX.Company.SearchByName}
      />}
      {items.length > 0 ? <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {items.map(contactWithWorkRelation =>
            <ContactWithWorkRelationRowCard
              key={contactWithWorkRelation.contact.contactId}
              company={props.company}
              contactWithWorkRelation={contactWithWorkRelation}
            />
          )}
        </div>
      </div> : <>
        <NoDataAvailableDiv />
      </>}
    </Panel>
  </>;
};

export default LinkedContacts;